import React, { useState, useEffect, useCallback } from 'react';
import { atelierAPI } from '../api';

/**
 * Launch Atelier — Maestra's internal creative-direction workspace.
 *
 * Four quiet tabs: Campaign Direction · Asset Generator · Saved Assets · Brand Core.
 * Strictly admin-only — the gate lives in the backend; the parent
 * `AdminPanel` only mounts this component when the active admin user
 * has the admin role.
 *
 * Visual language follows DESIGN_SYSTEM.md: cream/off-white backgrounds,
 * Playfair Display for editorial titles, Cormorant Garamond italic for
 * asides, generous spacing, no SaaS-style cards-with-shadow grids.
 */

const Atelier = {
  page: 'var(--kahla-cream, #faf8f5)',
  paper: '#ffffff',
  border: 'var(--kahla-border, #ece9e4)',
  borderSoft: 'rgba(28, 22, 14, 0.06)',
  text: 'var(--kahla-text, #2a2620)',
  textLight: 'var(--kahla-text-light, #8a847b)',
  charcoal: 'var(--kahla-charcoal, #1c160e)',
  accent: 'var(--kahla-taupe, #a39a8c)',
};

const TABS = [
  { key: 'campaigns',  label: 'Campaign Direction' },
  { key: 'generator',  label: 'Asset Generator' },
  { key: 'assets',     label: 'Saved Assets' },
  { key: 'references', label: 'Brand References' },
  { key: 'brand',      label: 'Brand Core' },
];

const LaunchAtelier = () => {
  const [tab, setTab] = useState('campaigns');

  return (
    <div data-testid="launch-atelier" style={{ background: Atelier.page }}>
      {/* Editorial header — sets the calm tone for the whole atelier */}
      <header style={{ padding: '48px 32px 32px', borderBottom: `1px solid ${Atelier.border}` }}>
        <p style={overline}>Admin · Internal</p>
        <h1 style={editorialTitle}>Launch Atelier</h1>
        <p style={lede}>
          A calm space for building Maestra's launch campaigns, founder messaging and visual direction —
          all anchored to the brand core, never to a content calendar.
        </p>
      </header>

      {/* Tabs — quiet, no SaaS underline animation */}
      <nav style={tabNav} role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`atelier-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            style={{
              ...tabBtn,
              color: tab === t.key ? Atelier.charcoal : Atelier.textLight,
              borderBottom: tab === t.key ? `1px solid ${Atelier.charcoal}` : '1px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div style={{ padding: '40px 32px 80px' }}>
        {tab === 'campaigns'  && <CampaignsTab />}
        {tab === 'generator'  && <GeneratorTab />}
        {tab === 'assets'     && <SavedAssetsTab />}
        {tab === 'references' && <BrandReferencesTab />}
        {tab === 'brand'      && <BrandCoreTab />}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// CAMPAIGNS
// ─────────────────────────────────────────────────────────────────────
const EMPTY_CAMPAIGN = {
  title: '', audience: '', emotional_direction: '', launch_angle: '',
  hook: '', cta: '', visual_direction: '', status: 'draft', notes: '',
};

const CampaignsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | {id, ...}

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await atelierAPI.listCampaigns(includeArchived);
    setItems(data.campaigns || []);
    setLoading(false);
  }, [includeArchived]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...EMPTY_CAMPAIGN, __new: true });

  const handleSave = async (form) => {
    if (form.__new) {
      const { __new, ...payload } = form;
      await atelierAPI.createCampaign(payload);
    } else {
      const { id, ...payload } = form;
      await atelierAPI.updateCampaign(id, payload);
    }
    setEditing(null);
    await load();
  };

  const handleDuplicate = async (id) => {
    await atelierAPI.duplicateCampaign(id);
    await load();
  };

  const handleArchive = async (item) => {
    if (!window.confirm(item.archived ? 'Restore this campaign?' : 'Archive this campaign?')) return;
    await atelierAPI.updateCampaign(item.id, { archived: !item.archived });
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign permanently? This cannot be undone.')) return;
    await atelierAPI.deleteCampaign(id);
    await load();
  };

  const handleExportPdf = async (item) => {
    const { data } = await atelierAPI.exportCampaignPdf(item.id);
    triggerDownload(data, `Maestra_${(item.title || 'campaign').replace(/[^a-z0-9_-]+/gi, '_')}.pdf`, 'application/pdf');
  };

  if (editing) {
    return (
      <CampaignEditor
        initial={editing}
        onCancel={() => setEditing(null)}
        onSave={handleSave}
      />
    );
  }

  return (
    <div>
      <div style={sectionHead}>
        <div>
          <h2 style={sectionTitle}>Campaigns</h2>
          <p style={sectionHelper}>
            Long-form launch concepts — each one is a direction document, not a to-do list.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <label style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: Atelier.textLight, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="campaigns-show-archived"
              style={{ marginRight: 6 }}
            />
            Include archived
          </label>
          <button type="button" onClick={startNew} style={primaryBtn} data-testid="campaign-new-btn">New campaign</button>
        </div>
      </div>

      {loading ? (
        <p style={muted}>Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState text="No campaigns yet. Start with a hook, a feeling, or a moment you want the launch to capture." />
      ) : (
        <div data-testid="campaigns-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 22 }}>
          {items.map(c => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onEdit={() => setEditing(c)}
              onDuplicate={() => handleDuplicate(c.id)}
              onArchive={() => handleArchive(c)}
              onDelete={() => handleDelete(c.id)}
              onExport={() => handleExportPdf(c)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CampaignCard = ({ campaign, onEdit, onDuplicate, onArchive, onDelete, onExport }) => (
  <article
    data-testid={`campaign-card-${campaign.id}`}
    style={{
      background: Atelier.paper,
      border: `1px solid ${Atelier.border}`,
      padding: '26px 28px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      minHeight: 240,
      opacity: campaign.archived ? 0.55 : 1,
    }}
  >
    <div>
      <p style={overlineSmall}>{(campaign.status || 'draft').toUpperCase()}{campaign.archived ? ' · Archived' : ''}</p>
      <h3 style={cardTitle}>{campaign.title || 'Untitled campaign'}</h3>
    </div>
    {campaign.hook && (
      <p style={{ fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: 15, color: Atelier.text, margin: 0, lineHeight: 1.5 }}>
        “{campaign.hook}”
      </p>
    )}
    {campaign.audience && (
      <p style={cardMeta}><span style={cardMetaLabel}>Audience</span> {campaign.audience}</p>
    )}
    {campaign.emotional_direction && (
      <p style={cardMeta}><span style={cardMetaLabel}>Feeling</span> {campaign.emotional_direction}</p>
    )}
    <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
      <button type="button" onClick={onEdit}      style={linkBtn} data-testid={`campaign-edit-${campaign.id}`}>Edit</button>
      <button type="button" onClick={onDuplicate} style={linkBtn}>Duplicate</button>
      <button type="button" onClick={onExport}    style={linkBtn}>Export PDF</button>
      <button type="button" onClick={onArchive}   style={linkBtn}>{campaign.archived ? 'Restore' : 'Archive'}</button>
      <button type="button" onClick={onDelete}    style={{ ...linkBtn, color: '#a85a5a' }}>Delete</button>
    </div>
  </article>
);

const CampaignEditor = ({ initial, onCancel, onSave }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div data-testid="campaign-editor" style={{ maxWidth: 880, margin: '0 auto' }}>
      <p style={overline}>{form.__new ? 'New campaign' : 'Editing campaign'}</p>
      <h2 style={editorialTitleSmall}>{form.title || 'Untitled'}</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 28 }}>
        <FieldGroup label="Title"
          helper="Give it a calm working name. Internal — clients never see this.">
          <input style={input} value={form.title || ''} onChange={e => set('title', e.target.value)} data-testid="campaign-field-title" />
        </FieldGroup>
        <FieldGroup label="Audience"
          helper="Who, specifically. One sentence. Not a persona — a moment in their week.">
          <textarea style={textarea} rows={2} value={form.audience || ''} onChange={e => set('audience', e.target.value)} data-testid="campaign-field-audience" />
        </FieldGroup>
        <FieldGroup label="Emotional direction"
          helper="One emotion. Relief, recognition, intimacy — choose one and stay there.">
          <input style={input} value={form.emotional_direction || ''} onChange={e => set('emotional_direction', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Launch angle"
          helper="The angle that makes this campaign different from a generic launch.">
          <textarea style={textarea} rows={3} value={form.launch_angle || ''} onChange={e => set('launch_angle', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Hook" helper="The line that opens the campaign. 8–14 words.">
          <input style={input} value={form.hook || ''} onChange={e => set('hook', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Call to action" helper="One verb. One outcome.">
          <input style={input} value={form.cta || ''} onChange={e => set('cta', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Visual direction"
          helper="The film-still your designer should aim for. Light, palette, composition.">
          <textarea style={textarea} rows={3} value={form.visual_direction || ''} onChange={e => set('visual_direction', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Notes" helper="Anything that belongs in this document but nowhere else yet.">
          <textarea style={textarea} rows={4} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
        </FieldGroup>
        <FieldGroup label="Status">
          <select style={input} value={form.status || 'draft'} onChange={e => set('status', e.target.value)}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
        </FieldGroup>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 18, marginTop: 32 }}>
        <button type="button" onClick={onCancel} style={linkBtn}>Cancel</button>
        <button type="button" onClick={handleSubmit} disabled={saving} style={primaryBtn} data-testid="campaign-save-btn">
          {saving ? 'Saving…' : (form.__new ? 'Create campaign' : 'Save changes')}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// ASSET GENERATOR  —  Campaign-driven (the primary surface)
//
// The founder never writes a prompt. They pick:
//   • Campaign         — the source of truth for everything below
//   • Output type      — Instagram carousel · Story sequence · Hero · …
//   • Visual mode      — Editorial · Quiet luxury · Documentary · …
//   • Platform         — Instagram · Website · Email · PDF · …
//   • Count            — 1 / 3 / 5 / 10
//
// Maestra composes the actual LLM prompt internally using the
// campaign's audience / emotion / launch angle, the Brand Core,
// the saved Brand References, and the output type's structure rules.
//
// A freeform "Advanced direction" panel exists for the rare moments
// a campaign doesn't capture something — but it's hidden by default
// so the surface never reads as a prompt playground.
// ─────────────────────────────────────────────────────────────────────
const GeneratorTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [options, setOptions] = useState({ output_types: [], visual_modes: [], platforms: [] });

  const [campaignId, setCampaignId] = useState('');
  const [outputType, setOutputType] = useState('instagram_carousel');
  const [visualMode, setVisualMode] = useState('editorial');
  const [platform, setPlatform] = useState('instagram');
  const [count, setCount] = useState(3);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extra, setExtra] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Brand references (auto-attached when always_include is on; founder
  // can override per-render from the grounding panel below). Image
  // outputs reach for these; text outputs don't.
  const [allReferences, setAllReferences] = useState([]);
  const [selectedRefIds, setSelectedRefIds] = useState(new Set());
  const alwaysOnRefs = allReferences.filter(r => r.always_include);

  // Bootstrap: campaigns + catalogues + references in parallel.
  useEffect(() => {
    Promise.all([
      atelierAPI.listCampaigns(false),
      fetch(undefined),  // placeholder; replaced below
    ]).catch(() => {});
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [campaignsRes, optionsRes, refsRes] = await Promise.all([
          atelierAPI.listCampaigns(false),
          atelierAPI.generationOptions(),
          atelierAPI.listBrandReferences(),
        ]);
        if (cancelled) return;
        const list = campaignsRes.data?.campaigns || [];
        setCampaigns(list);
        if (list[0] && !campaignId) setCampaignId(list[0].id);
        setOptions(optionsRes.data || { output_types: [], visual_modes: [], platforms: [] });
        setAllReferences(refsRes.data?.references || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load generator options');
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentCampaign = campaigns.find(c => c.id === campaignId);
  const currentOutput = options.output_types.find(t => t.key === outputType);
  const outputKind = currentOutput?.output_kind; // 'text' | 'image' | 'mixed'
  const showReferences = outputKind === 'image' || outputKind === 'mixed';

  const runGenerate = async () => {
    if (!campaignId) {
      setError('Pick a campaign first.');
      return;
    }
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await atelierAPI.generateFromCampaign({
        campaign_id: campaignId,
        output_type: outputType,
        visual_mode: visualMode,
        platform: platform,
        count: Number(count) || 3,
        extra_direction: extra.trim() || undefined,
        reference_ids: selectedRefIds.size > 0 ? Array.from(selectedRefIds) : undefined,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={sectionTitle}>Generate from Campaign</h2>
      <p style={sectionHelper}>
        Pick a campaign and an output. Maestra composes the rest — voice, brand grounding,
        platform spec, visual mode — using the campaign's emotional reality and the Brand Core.
        You never write a prompt.
      </p>

      {campaigns.length === 0 ? (
        <div style={{ marginTop: 28 }}>
          <EmptyState text="No campaigns yet. Open the Campaign Direction tab and create one first — every generation starts from a campaign's emotional brief." />
        </div>
      ) : (
        <section style={{ marginTop: 28, padding: 28, background: Atelier.paper, border: `1px solid ${Atelier.border}` }}>
          {/* ── Step 1: Campaign ───────────────────────────────────── */}
          <FieldGroup label="1. Campaign" helper="The source of truth for everything below.">
            <select
              style={input}
              value={campaignId}
              onChange={e => setCampaignId(e.target.value)}
              data-testid="gen-campaign-select"
            >
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.title || 'Untitled campaign'}</option>
              ))}
            </select>
          </FieldGroup>

          {/* Live campaign context preview — confirms what Maestra
              will read as the brief. Calms the user that the system
              actually knows what it's about to generate from. */}
          {currentCampaign && (
            <div data-testid="gen-campaign-context" style={{ marginTop: 14, padding: '14px 18px', background: Atelier.page, border: `1px dashed ${Atelier.border}` }}>
              <p style={overlineSmall}>Maestra reads from this campaign</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', marginTop: 10, fontSize: 13, color: Atelier.text, lineHeight: 1.55 }}>
                {currentCampaign.audience            && <div><span style={cardMetaLabel}>Audience</span>{currentCampaign.audience}</div>}
                {currentCampaign.emotional_direction && <div><span style={cardMetaLabel}>Feeling</span>{currentCampaign.emotional_direction}</div>}
                {currentCampaign.hook                && <div><span style={cardMetaLabel}>Hook</span>{currentCampaign.hook}</div>}
                {currentCampaign.cta                 && <div><span style={cardMetaLabel}>CTA</span>{currentCampaign.cta}</div>}
              </div>
            </div>
          )}

          {/* ── Step 2 / 3 / 4 grid ────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 24 }}>
            <FieldGroup label="2. Output">
              <select style={input} value={outputType} onChange={e => setOutputType(e.target.value)} data-testid="gen-output-select">
                {options.output_types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </FieldGroup>
            <FieldGroup label="3. Visual mode" helper="Aesthetic vocabulary applied to any image output.">
              <select style={input} value={visualMode} onChange={e => setVisualMode(e.target.value)} data-testid="gen-mode-select">
                {options.visual_modes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </FieldGroup>
            <FieldGroup label="4. Platform" helper="Drives crop / aspect ratio for image outputs.">
              <select style={input} value={platform} onChange={e => setPlatform(e.target.value)} data-testid="gen-platform-select">
                {options.platforms.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </FieldGroup>
            <FieldGroup label="5. Count" helper="How many variants to compose. Up to 10.">
              <select style={input} value={count} onChange={e => setCount(Number(e.target.value))}>
                {[1, 3, 5, 10].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </FieldGroup>
          </div>

          {/* ── Brand references — only shown for image-producing outputs */}
          {showReferences && (
            <div
              data-testid="gen-refs-panel"
              style={{
                marginTop: 22,
                padding: '14px 18px',
                background: Atelier.page,
                border: `1px dashed ${Atelier.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <p style={overlineSmall}>Brand grounding</p>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.textLight, margin: '4px 0 0' }}>
                    {selectedRefIds.size > 0
                      ? `${selectedRefIds.size} reference${selectedRefIds.size === 1 ? '' : 's'} selected for this render.`
                      : alwaysOnRefs.length > 0
                        ? `${alwaysOnRefs.length} always-on reference${alwaysOnRefs.length === 1 ? '' : 's'} will be attached automatically.`
                        : 'No references yet. Maestra will avoid depicting branded elements entirely.'}
                  </p>
                </div>
                {selectedRefIds.size > 0 && (
                  <button type="button" style={linkBtn} onClick={() => setSelectedRefIds(new Set())}>Clear selection</button>
                )}
              </div>
              {allReferences.length > 0 && (
                <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  {allReferences.slice(0, 12).map(r => {
                    const isSelected = selectedRefIds.has(r.id);
                    const isAuto = r.always_include && selectedRefIds.size === 0;
                    const ring = isSelected ? Atelier.charcoal : (isAuto ? Atelier.accent : Atelier.border);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedRefIds(prev => {
                          const next = new Set(prev);
                          if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                          return next;
                        })}
                        data-testid={`gen-ref-pick-${r.id}`}
                        title={`${(r.category || '').replace(/_/g, ' ')}${r.note ? ' · ' + r.note : ''}${r.always_include ? ' · always-on' : ''}`}
                        style={{ padding: 2, border: `1.5px solid ${ring}`, background: '#fff', cursor: 'pointer', width: 64, height: 64, overflow: 'hidden', transition: 'border-color 0.18s' }}
                      >
                        <img src={r.image_data_url} alt={r.category} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Advanced direction (collapsed by default) ─────────── */}
          <div style={{ marginTop: 22 }}>
            <button
              type="button"
              onClick={() => setShowAdvanced(v => !v)}
              style={{ ...linkBtn, textDecoration: 'none' }}
              data-testid="gen-advanced-toggle"
            >
              {showAdvanced ? '— Hide advanced direction' : '+ Advanced direction (optional)'}
            </button>
            {showAdvanced && (
              <div style={{ marginTop: 12 }}>
                <FieldGroup
                  label="Extra direction"
                  helper="Use this only when the campaign doesn't capture something. The campaign brief is already Maestra's primary source."
                >
                  <textarea
                    style={textarea}
                    rows={3}
                    value={extra}
                    onChange={e => setExtra(e.target.value)}
                    placeholder="Optional"
                    data-testid="gen-extra-direction"
                  />
                </FieldGroup>
              </div>
            )}
          </div>

          {error && <p style={{ color: '#a85a5a', marginTop: 14, fontSize: 13 }}>{error}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 22 }}>
            <button
              type="button"
              onClick={runGenerate}
              disabled={loading || !campaignId}
              style={loading || !campaignId ? { ...primaryBtn, opacity: 0.4, cursor: 'default' } : primaryBtn}
              data-testid="gen-from-campaign-btn"
            >
              {loading ? 'Composing…' : 'Generate from Campaign'}
            </button>
          </div>
        </section>
      )}

      {result && <CampaignGenerationResult result={result} />}
    </div>
  );
};

const CampaignGenerationResult = ({ result }) => {
  const c = result.campaign || {};
  return (
    <section data-testid="gen-results" style={{ marginTop: 36 }}>
      {/* "Generated from:" header per the brief — each result feels
          connected to a larger launch ecosystem, never standalone. */}
      <div style={{ padding: '14px 18px', background: Atelier.page, border: `1px solid ${Atelier.border}` }}>
        <p style={overlineSmall}>Generated from</p>
        <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, color: Atelier.charcoal, margin: '4px 0 6px' }}>
          {c.title || 'Campaign'} · {result.output_type_label}
        </p>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.textLight, margin: 0 }}>
          {[c.emotional_direction, result.visual_mode_label, result.platform_label]
            .filter(Boolean).join(' · ')}
          {c.audience && <span> · <span style={{ color: Atelier.text }}>{c.audience}</span></span>}
        </p>
      </div>

      {result.items && result.items.length > 0 && (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {result.items.map((it, idx) => (
            <CampaignResultText key={idx} item={it} index={idx} />
          ))}
        </div>
      )}

      {result.images && result.images.length > 0 && (
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {result.images.map(img => (
            <div key={img.id} style={{ background: Atelier.paper, border: `1px solid ${Atelier.border}` }}>
              <img src={img.image_data_url} alt={img.title} style={{ width: '100%', display: 'block' }} />
              <p style={{ ...overlineSmall, padding: '10px 14px 12px' }}>{img.title}</p>
            </div>
          ))}
          <p style={{ ...muted, gridColumn: '1 / -1', textAlign: 'right', margin: 0 }}>
            Images are auto-saved to Saved Assets and linked to this campaign.
          </p>
        </div>
      )}
    </section>
  );
};

const CampaignResultText = ({ item, index }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = typeof item === 'string' ? item : JSON.stringify(item, null, 2);
      const title = typeof item === 'string'
        ? item.slice(0, 80)
        : (item.title || item.headline || item.concept || `Asset ${index + 1}`);
      await atelierAPI.createAsset({ type: 'generated', title, content });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const isString = typeof item === 'string';
  return (
    <div style={{ background: Atelier.paper, border: `1px solid ${Atelier.border}`, padding: '20px 24px' }}>
      {isString ? (
        <p style={{ margin: 0, fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontSize: 17, fontStyle: 'italic', color: Atelier.charcoal, lineHeight: 1.55 }}>“{item}”</p>
      ) : (
        <CampaignResultStructured item={item} />
      )}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || saved}
          style={{ ...linkBtn, opacity: saved ? 0.6 : 1 }}
        >
          {saved ? 'Saved' : (saving ? 'Saving…' : 'Save to Assets')}
        </button>
      </div>
    </div>
  );
};

/** Pretty-print structured output (carousel slides / story frames /
 *  hero concepts / deck sections) instead of dumping raw JSON. The
 *  founder reads results as editorial drafts, not API payloads. */
const CampaignResultStructured = ({ item }) => {
  if (Array.isArray(item.slides)) {
    return (
      <div>
        {item.title && <h4 style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 400, color: Atelier.charcoal, margin: '0 0 14px' }}>{item.title}</h4>}
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {item.slides.map((s, i) => (
            <li key={i} style={{ paddingLeft: 22, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 0, top: 2, fontSize: 10, letterSpacing: '0.16em', color: Atelier.textLight }}>{String(i + 1).padStart(2, '0')}</span>
              <p style={{ fontSize: 13, fontWeight: 600, color: Atelier.charcoal, margin: 0 }}>{s.heading}</p>
              <p style={{ fontSize: 13, color: Atelier.text, margin: '2px 0 0', lineHeight: 1.55 }}>{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (Array.isArray(item.frames)) {
    return (
      <div>
        {item.title && <h4 style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 400, color: Atelier.charcoal, margin: '0 0 12px' }}>{item.title}</h4>}
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.frames.map((f, i) => (
            <li key={i} style={{ fontSize: 13, color: Atelier.text, paddingLeft: 22, position: 'relative' }}>
              <span style={{ position: 'absolute', left: 0, top: 0, fontSize: 10, letterSpacing: '0.16em', color: Atelier.textLight }}>{String(i + 1).padStart(2, '0')}</span>
              {typeof f === 'string' ? f : (f.caption || JSON.stringify(f))}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  // Hero concept (overline/headline/subline/cta/visual_world) or deck.
  if (item.headline || item.overline) {
    return (
      <div>
        {item.overline && <p style={overlineSmall}>{item.overline}</p>}
        {item.headline && <h4 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 400, color: Atelier.charcoal, margin: '6px 0 8px' }}>{item.headline}</h4>}
        {item.subline && <p style={{ fontSize: 14, color: Atelier.text, margin: '0 0 10px', lineHeight: 1.6 }}>{item.subline}</p>}
        {item.cta && <p style={{ fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase', color: Atelier.charcoal, margin: '0 0 10px' }}>CTA · {item.cta}</p>}
        {item.visual_world && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.textLight, margin: 0, lineHeight: 1.55 }}>{item.visual_world}</p>}
      </div>
    );
  }
  // Fallback: pretty JSON.
  return (
    <pre style={{ margin: 0, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 12, color: Atelier.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
      {JSON.stringify(item, null, 2)}
    </pre>
  );
};

// ─────────────────────────────────────────────────────────────────────
// SAVED ASSETS
// ─────────────────────────────────────────────────────────────────────
const SavedAssetsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await atelierAPI.listAssets({
      type: filter || undefined,
      favorite_only: favoriteOnly,
    });
    setItems(data.assets || []);
    setLoading(false);
  }, [filter, favoriteOnly]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleFavorite = async (item) => {
    await atelierAPI.updateAsset(item.id, { favorite: !item.favorite });
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    await atelierAPI.deleteAsset(id);
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    await load();
  };

  const handleDuplicate = async (id) => {
    await atelierAPI.duplicateAsset(id);
    await load();
  };

  const exportSelectedZip = async () => {
    if (selected.size === 0) return;
    const { data } = await atelierAPI.exportAssetsZip(Array.from(selected));
    triggerDownload(data, 'Maestra_Atelier_Assets.zip', 'application/zip');
  };

  return (
    <div>
      <div style={sectionHead}>
        <div>
          <h2 style={sectionTitle}>Saved Assets</h2>
          <p style={sectionHelper}>Every saved hook, post, concept, and image — your living launch library.</p>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={input} value={filter} onChange={e => setFilter(e.target.value)} data-testid="assets-filter-type">
            <option value="">All types</option>
            <option value="generated">Generated (any)</option>
            <option value="hook">Launch hook</option>
            <option value="waitlist">Waitlist concept</option>
            <option value="founder_quote">Founder quote</option>
            <option value="emotional_direction">Emotional direction</option>
            <option value="image">Image</option>
          </select>
          <label style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: Atelier.textLight, cursor: 'pointer' }}>
            <input type="checkbox" checked={favoriteOnly} onChange={(e) => setFavoriteOnly(e.target.checked)} style={{ marginRight: 6 }} />
            Favorites only
          </label>
          <button
            type="button"
            onClick={exportSelectedZip}
            disabled={selected.size === 0}
            style={selected.size === 0 ? { ...primaryBtn, opacity: 0.4, cursor: 'default' } : primaryBtn}
            data-testid="assets-export-zip"
          >
            Export ZIP ({selected.size})
          </button>
        </div>
      </div>

      {loading ? (
        <p style={muted}>Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState text="Nothing here yet. Use the Asset Generator to compose something — saved items will appear here." />
      ) : (
        <div data-testid="assets-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 18 }}>
          {items.map(a => (
            <AssetCard
              key={a.id}
              asset={a}
              selected={selected.has(a.id)}
              onToggleSelect={() => toggleSelect(a.id)}
              onToggleFavorite={() => toggleFavorite(a)}
              onDelete={() => handleDelete(a.id)}
              onDuplicate={() => handleDuplicate(a.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AssetCard = ({ asset, selected, onToggleSelect, onToggleFavorite, onDelete, onDuplicate }) => {
  const isImage = asset.type === 'image' && asset.image_data_url;
  return (
    <article
      data-testid={`asset-card-${asset.id}`}
      style={{
        background: Atelier.paper,
        border: selected ? `1px solid ${Atelier.charcoal}` : `1px solid ${Atelier.border}`,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color 0.18s',
      }}
    >
      {isImage && (
        <img
          src={asset.image_data_url}
          alt={asset.title || 'Generated visual'}
          style={{ width: '100%', display: 'block', maxHeight: 360, objectFit: 'cover' }}
        />
      )}
      <div style={{ padding: '18px 22px 16px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <p style={overlineSmall}>
          {(asset.type || '').replace(/_/g, ' ')}
          {asset.favorite && <span style={{ marginLeft: 10, color: Atelier.charcoal }}>★ Favorite</span>}
        </p>
        <h4 style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, fontWeight: 400, color: Atelier.charcoal, margin: 0 }}>
          {asset.title || 'Untitled asset'}
        </h4>
        {!isImage && asset.content && (
          <p style={{ fontSize: 13, color: Atelier.text, margin: 0, lineHeight: 1.55, maxHeight: 110, overflow: 'hidden' }}>
            {asset.content.length > 220 ? asset.content.slice(0, 220) + '…' : asset.content}
          </p>
        )}
        <div style={{ marginTop: 'auto', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: Atelier.textLight, cursor: 'pointer' }}>
            <input type="checkbox" checked={selected} onChange={onToggleSelect} style={{ marginRight: 6 }} />
            Select
          </label>
          <button type="button" onClick={onToggleFavorite} style={linkBtn}>{asset.favorite ? 'Unfavorite' : 'Favorite'}</button>
          <button type="button" onClick={onDuplicate} style={linkBtn}>Duplicate</button>
          <button type="button" onClick={onDelete} style={{ ...linkBtn, color: '#a85a5a' }}>Delete</button>
        </div>
      </div>
    </article>
  );
};

// ─────────────────────────────────────────────────────────────────────
// BRAND CORE
// ─────────────────────────────────────────────────────────────────────
const BRAND_FIELDS = [
  { key: 'is',                    label: 'What Maestra IS',                helper: 'The clearest definition of the product, in one paragraph. Use this whenever you doubt a campaign.' },
  { key: 'is_not',                label: 'What Maestra is NOT',            helper: 'The boundaries. If a campaign drifts here, kill it.' },
  { key: 'founder_identity',      label: 'Founder identity',               helper: 'The real founder context — one person, photographer, visual creative. Maestra never invents a team or agency. Flows into every generation alongside an immutable identity guard.' },
  { key: 'emotional_direction',   label: 'Emotional direction',            helper: 'The feeling every Maestra touchpoint should leave behind.' },
  { key: 'tone_of_voice',         label: 'Tone of voice',                  helper: 'How Maestra speaks — and what it never says.' },
  { key: 'visual_language',       label: 'Visual language',                helper: 'Type, palette, composition, light. Editorial first.' },
  { key: 'ux_philosophy',         label: 'UX philosophy',                  helper: 'How the product should feel to use, not what it should do.' },
  { key: 'forbidden_styles',      label: 'Forbidden styles',               helper: 'The list of aesthetics Maestra never adopts.' },
  { key: 'campaign_references',   label: 'Campaign references',            helper: 'External work that captures the right feeling. Refresh quarterly.' },
  { key: 'editorial_principles',  label: 'Editorial principles',           helper: 'The five rules every piece of Maestra copy lives by.' },
];

// ─────────────────────────────────────────────────────────────────────
// BRAND REFERENCES — uploaded REAL Maestra assets that ground image gen
// ─────────────────────────────────────────────────────────────────────
const REFERENCE_CATEGORIES = [
  { key: 'logo',          label: 'Logo' },
  { key: 'typography',    label: 'Typography' },
  { key: 'screenshot',    label: 'Screenshot (app)' },
  { key: 'exported_pdf',  label: 'Exported PDF' },
  { key: 'board_card',    label: 'Board card' },
  { key: 'client_view',   label: 'Client view' },
  { key: 'ui_fragment',   label: 'UI fragment' },
  { key: 'reaction',      label: 'Reaction' },
  { key: 'footer',        label: 'Footer / signature' },
  { key: 'color_palette', label: 'Color palette' },
  { key: 'other',         label: 'Other' },
];

const BrandReferencesTab = () => {
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  // Upload form state
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('logo');
  const [note, setNote] = useState('');
  const [alwaysInclude, setAlwaysInclude] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await atelierAPI.listBrandReferences();
    setRefs(data.references || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setUploadError(null);
    try {
      await atelierAPI.uploadBrandReference(file, { category, note, always_include: alwaysInclude });
      setFile(null); setNote(''); setAlwaysInclude(true); setCategory('logo');
      // Reset the actual file input so a re-upload of the same filename re-fires onChange.
      const input = document.querySelector('[data-testid="ref-upload-input"]');
      if (input) input.value = '';
      await load();
    } catch (e) {
      setUploadError(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleAlwaysInclude = async (r) => {
    await atelierAPI.updateBrandReference(r.id, { always_include: !r.always_include });
    await load();
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete this ${(r.category || '').replace(/_/g, ' ')} reference?`)) return;
    await atelierAPI.deleteBrandReference(r.id);
    await load();
  };

  const visible = filter ? refs.filter(r => r.category === filter) : refs;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={sectionTitle}>Brand References</h2>
      <p style={sectionHelper}>
        The real Maestra assets the image generator should ground to. Upload your logo, real client-view screenshots,
        exported board PDFs, board cards, footer fragments — anything an AI might otherwise invent.
      </p>

      {/* ── Upload card ───────────────────────────────────────── */}
      <section style={{ marginTop: 28, padding: 28, background: Atelier.paper, border: `1px solid ${Atelier.border}` }}>
        <p style={overlineSmall}>Add a reference</p>
        <p style={{ ...sectionHelper, marginTop: 6 }}>
          PNG, JPG, WEBP up to 6 MB. PDFs are auto-converted to their first page.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 18 }}>
          <FieldGroup label="File">
            <input
              data-testid="ref-upload-input"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ fontSize: 13, fontFamily: 'inherit' }}
            />
          </FieldGroup>
          <FieldGroup label="Category">
            <select style={input} value={category} onChange={e => setCategory(e.target.value)} data-testid="ref-category-select">
              {REFERENCE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </FieldGroup>
        </div>
        <FieldGroup label="Note" helper="Optional. e.g. 'Wordmark on cream. Use full bleed, no rotation.'">
          <input style={input} value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" data-testid="ref-note-input" />
        </FieldGroup>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 12, color: Atelier.textLight, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={alwaysInclude}
            onChange={(e) => setAlwaysInclude(e.target.checked)}
            data-testid="ref-always-include"
          />
          <span style={{ letterSpacing: '0.10em', textTransform: 'uppercase' }}>Always include in image generations</span>
        </label>
        {uploadError && <p style={{ color: '#a85a5a', fontSize: 13, marginTop: 12 }}>{uploadError}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            style={!file || uploading ? { ...primaryBtn, opacity: 0.4, cursor: 'default' } : primaryBtn}
            data-testid="ref-upload-btn"
          >
            {uploading ? 'Uploading…' : 'Add reference'}
          </button>
        </div>
      </section>

      {/* ── Library ─────────────────────────────────────────────── */}
      <div style={{ ...sectionHead, marginTop: 38 }}>
        <div>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 400, color: Atelier.charcoal, margin: 0 }}>Library</h3>
          <p style={sectionHelper}>Drag-pick from the Asset Generator's grounding panel to use a subset for a single render.</p>
        </div>
        <select style={input} value={filter} onChange={e => setFilter(e.target.value)} data-testid="ref-filter-category">
          <option value="">All categories</option>
          {REFERENCE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      {loading ? (
        <p style={muted}>Loading…</p>
      ) : visible.length === 0 ? (
        <EmptyState text="No references yet. Add your logo and a real client-view screenshot first — they're the highest-leverage grounding signals." />
      ) : (
        <div data-testid="references-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {visible.map(r => (
            <article
              key={r.id}
              data-testid={`reference-card-${r.id}`}
              style={{ background: Atelier.paper, border: `1px solid ${Atelier.border}`, display: 'flex', flexDirection: 'column' }}
            >
              <img
                src={r.image_data_url}
                alt={r.category}
                style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block', background: Atelier.page }}
              />
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={overlineSmall}>
                  {(r.category || '').replace(/_/g, ' ')}
                  {r.always_include && <span style={{ marginLeft: 10, color: Atelier.accent }}>· always-on</span>}
                </p>
                {r.note && (
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.text, margin: 0, lineHeight: 1.5 }}>
                    {r.note}
                  </p>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" style={linkBtn} onClick={() => toggleAlwaysInclude(r)} data-testid={`reference-toggle-${r.id}`}>
                    {r.always_include ? 'Make on-demand' : 'Make always-on'}
                  </button>
                  <button type="button" style={{ ...linkBtn, color: '#a85a5a' }} onClick={() => handleDelete(r)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

const BrandCoreTab = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await atelierAPI.getBrandCore();
    setForm(data || {});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip Mongo-only keys before sending; the server upserts the rest.
      const { _id, _key, updated_at, ...payload } = form;
      const { data } = await atelierAPI.updateBrandCore(payload);
      setForm(data || form);
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={muted}>Loading…</p>;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <h2 style={sectionTitle}>Brand Core</h2>
      <p style={sectionHelper}>
        The permanent source of truth for Maestra's identity. Every Asset Generator prompt is prefaced with this.
      </p>

      {/* Immutable founder-identity guard notice — explains the safety
          rail without exposing the raw rules text. Cannot be edited. */}
      <div
        data-testid="founder-guard-notice"
        style={{
          marginTop: 24,
          padding: '14px 18px',
          background: Atelier.page,
          border: `1px dashed ${Atelier.border}`,
        }}
      >
        <p style={overlineSmall}>Founder identity guard · immutable</p>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.textLight, margin: '6px 0 0', lineHeight: 1.6 }}>
          Every generation is hard-wrapped with a non-editable identity rule:
          Maestra is built by ONE founder — a photographer, visual creative, aesthetic systems thinker.
          The system will refuse to invent fake teams, agencies, scaling stories, or plural "we" language.
          This rule cannot be removed; the editable <em>Founder identity</em> field below adds
          personal context on top of it.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, marginTop: 32 }}>
        {BRAND_FIELDS.map(f => (
          <FieldGroup key={f.key} label={f.label} helper={f.helper}>
            <textarea
              style={{ ...textarea, minHeight: f.key === 'editorial_principles' ? 140 : 100 }}
              rows={f.key === 'editorial_principles' ? 7 : 5}
              value={form[f.key] || ''}
              onChange={e => set(f.key, e.target.value)}
              data-testid={`brand-${f.key}`}
            />
          </FieldGroup>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 18, marginTop: 32 }}>
        {savedAt && <span style={{ ...muted, color: Atelier.textLight }}>Saved {savedAt.toLocaleTimeString()}</span>}
        <button type="button" onClick={handleSave} disabled={saving} style={primaryBtn} data-testid="brand-save-btn">
          {saving ? 'Saving…' : 'Save Brand Core'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────────
const FieldGroup = ({ label, helper, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: Atelier.textLight, marginBottom: 4 }}>
      {label}
    </label>
    {helper && (
      <p style={{ fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: 13, color: Atelier.textLight, margin: '0 0 10px', lineHeight: 1.5 }}>
        {helper}
      </p>
    )}
    {children}
  </div>
);

const EmptyState = ({ text }) => (
  <div style={{ padding: '60px 32px', border: `1px dashed ${Atelier.border}`, background: Atelier.paper, textAlign: 'center' }}>
    <p style={{ fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: 15, color: Atelier.textLight, margin: 0, lineHeight: 1.6 }}>
      {text}
    </p>
  </div>
);

function triggerDownload(blob, filename, mime) {
  const url = window.URL.createObjectURL(new Blob([blob], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 2000);
}

// ─── Style primitives (kept inline to avoid touching App.css) ───
const overline = { fontFamily: 'Playfair Display, serif', fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: Atelier.textLight, margin: '0 0 12px', fontWeight: 500 };
const overlineSmall = { fontFamily: 'Playfair Display, serif', fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: Atelier.textLight, margin: 0, fontWeight: 500 };
const editorialTitle = { fontFamily: 'Playfair Display, Georgia, serif', fontSize: 38, fontWeight: 400, color: Atelier.charcoal, margin: '0 0 14px', letterSpacing: '-0.01em' };
const editorialTitleSmall = { fontFamily: 'Playfair Display, Georgia, serif', fontSize: 28, fontWeight: 400, color: Atelier.charcoal, margin: '4px 0 0' };
const lede = { fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: 16, color: Atelier.textLight, margin: 0, maxWidth: 640, lineHeight: 1.6 };
const sectionTitle = { fontFamily: 'Playfair Display, Georgia, serif', fontSize: 24, fontWeight: 400, color: Atelier.charcoal, margin: 0 };
const sectionHelper = { fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic', fontSize: 14, color: Atelier.textLight, margin: '6px 0 0', lineHeight: 1.55, maxWidth: 600 };
const sectionHead = { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, marginBottom: 28, flexWrap: 'wrap' };
const cardTitle = { fontFamily: 'Playfair Display, serif', fontSize: 19, fontWeight: 400, color: Atelier.charcoal, margin: '4px 0 0' };
const cardMeta = { fontSize: 13, color: Atelier.text, margin: 0, lineHeight: 1.55 };
const cardMetaLabel = { fontFamily: 'Inter, sans-serif', fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: Atelier.textLight, marginRight: 8 };
const tabNav = { display: 'flex', gap: 32, padding: '0 32px', borderBottom: `1px solid ${Atelier.border}` };
const tabBtn = { background: 'none', border: 'none', borderBottom: '1px solid transparent', padding: '18px 0 16px', fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase', cursor: 'pointer', transition: 'color 0.18s, border-color 0.18s' };
const input = { width: '100%', padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', border: `1px solid ${Atelier.border}`, background: '#fff', color: Atelier.text, boxSizing: 'border-box' };
const textarea = { ...input, resize: 'vertical', minHeight: 80, lineHeight: 1.5 };
const primaryBtn = { background: Atelier.charcoal, color: '#fff', border: 'none', padding: '11px 22px', fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase', fontFamily: 'inherit', cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase', color: Atelier.textLight, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 };
const muted = { fontSize: 13, color: Atelier.textLight, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, serif' };

export default LaunchAtelier;
